import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Rocket, Trash2, ExternalLink, Coins, Heart, Eye, Clock, Plus, BarChart2, Star, RefreshCw, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { devMyApps, devDeleteApp, buyFeaturedSlot, devOneClickUpdate, formatDownloads, formatApiError } from "@/lib/api";
import Footer from "@/components/Footer";
import { DEV, FEATURED } from "@/constants/testIds";

export default function DevDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [updatingSlug, setUpdatingSlug] = useState(null);

  const load = () => devMyApps().then(setData).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));

  useEffect(() => { load(); }, []);

  const boost = async (slug, name) => {
    if (!window.confirm(`Feature "${name}" on the homepage for 7 days? Costs 500 coins.`)) return;
    try {
      await buyFeaturedSlot(slug);
      toast.success(`Boosted! "${name}" is featured for 7 days`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const quickUpdate = async (slug, name) => {
    if (!window.confirm(`Publish a fresh update for "${name}"?\n\n• Version will bump (patch)\n• We'll re-fetch the manifest/screenshots if it's a URL app\n• Followers get a push notification`)) return;
    setUpdatingSlug(slug);
    try {
      const res = await devOneClickUpdate(slug);
      toast.success(`Updated to v${res.version}`, {
        description: res.refreshed_from_url ? "Refreshed screenshots + manifest" : "Version bumped",
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUpdatingSlug(null);
    }
  };

  if (!user) return null;

  const remove = async (slug, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await devDeleteApp(slug);
      toast.success("App removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid={DEV.dashboardRoot}>
      <div className="max-w-6xl mx-auto px-6 pt-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">// creator dashboard</div>
            <h1 className="font-heading font-black text-5xl uppercase tracking-tighter">Your Apps</h1>
          </div>
          <Link to="/dev/publish" className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest px-5 py-2.5 rounded-full">
            <Plus className="w-4 h-4" /> Publish New
          </Link>
        </div>

        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
            <Stat label="Apps Live" value={data.totals.apps} accent="#00F0FF" icon={<Rocket className="w-4 h-4" />} testId="dev-stat-apps" />
            <Stat label="Views" value={formatDownloads(data.totals.views || 0)} accent="#39FF14" icon={<Eye className="w-4 h-4" />} testId="dev-stat-views" />
            <Stat label="Favorites" value={formatDownloads(data.totals.favorites || 0)} accent="#FF003C" icon={<Heart className="w-4 h-4" />} testId="dev-stat-favorites" />
            <Stat label="Avg Play Time" value={`${data.totals.avg_play_time_minutes || 18}m`} accent="#00F0FF" icon={<Clock className="w-4 h-4" />} testId="dev-stat-play-time" />
            <Stat label="Tips Received" value={formatDownloads(data.totals.tips)} accent="#FFE300" icon={<Coins className="w-4 h-4" />} testId="dev-stat-tips" />
          </div>
        )}

        <div className="mt-8 space-y-3">
          {data?.apps.length === 0 && (
            <div className="glass rounded-2xl p-10 text-center">
              <p className="text-white/60 mb-4">You haven't published anything yet.</p>
              <Link to="/dev/publish" className="inline-flex items-center gap-2 bg-[#00F0FF] text-black font-bold mono text-xs uppercase tracking-widest px-5 py-2.5 rounded-full">
                <Plus className="w-4 h-4" /> Publish Your First App
              </Link>
            </div>
          )}
          {data?.apps.map((a) => (
            <div key={a.slug} data-testid={`${DEV.appRow}-${a.slug}`} className="glass rounded-2xl p-4 flex items-center gap-4">
              <img src={a.icon_url || "https://via.placeholder.com/60"} alt="" className="w-14 h-14 rounded-xl object-cover ring-1 ring-white/10" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Link to={`/app/${a.slug}`} className="font-heading font-bold text-lg truncate hover:text-[#00F0FF]">{a.name}</Link>
                  {a.is_pwa && <span className="mono text-[9px] uppercase bg-[#39FF14]/15 text-[#39FF14] px-1.5 py-0.5 rounded">PWA</span>}
                  {a.source?.mode && <span className="mono text-[9px] uppercase text-white/40">· {a.source.mode}</span>}
                </div>
                <div className="mono text-[10px] text-white/50 truncate mt-0.5">{a.tagline || a.description?.slice(0, 100)}</div>
                <div className="flex gap-3 mt-1 mono text-[10px] text-white/40">
                  <span>v{a.version}</span>
                  <span>· {a.category}</span>
                  <span>· {formatDownloads(a.views || 0)} views</span>
                  <span>· {formatDownloads(a.favorites || 0)} favs</span>
                  <span>· {a.avg_play_time_minutes || 18}m avg</span>
                  <span>· {a.tips_received} coins</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`dev-update-${a.slug}`}
                  onClick={() => quickUpdate(a.slug, a.name)}
                  disabled={updatingSlug === a.slug}
                  title="One-click update — bump version, refresh manifest, notify followers"
                  className="h-9 px-3 rounded-lg border border-[#39FF14]/40 hover:bg-[#39FF14]/10 flex items-center gap-1.5 text-[#39FF14] disabled:opacity-40 mono text-[10px] uppercase tracking-widest font-bold">
                  {updatingSlug === a.slug
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <RefreshCw className="w-3.5 h-3.5" />}
                  {updatingSlug === a.slug ? "…" : "Update"}
                </button>
                <button data-testid={FEATURED.boostBtn} onClick={() => boost(a.slug, a.name)} title="Boost — 500 coins for 7 days on Home"
                  className="w-9 h-9 rounded-lg border border-[#FFE300]/40 hover:bg-[#FFE300]/10 flex items-center justify-center text-[#FFE300]">
                  <Star className="w-4 h-4" />
                </button>
                <Link to={`/dev/apps/${a.slug}/analytics`} className="w-9 h-9 rounded-lg border border-white/10 hover:border-[#00F0FF]/50 flex items-center justify-center text-white/60 hover:text-[#00F0FF]" title="Analytics">
                  <BarChart2 className="w-4 h-4" />
                </Link>
                {a.launch_url && (
                  <a href={a.launch_url} target="_blank" rel="noopener" className="w-9 h-9 rounded-lg border border-white/10 hover:border-[#00F0FF]/50 flex items-center justify-center text-white/60 hover:text-[#00F0FF]">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button onClick={() => remove(a.slug, a.name)} className="w-9 h-9 rounded-lg border border-white/10 hover:border-[#FF003C]/50 flex items-center justify-center text-white/60 hover:text-[#FF003C]">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Stat({ label, value, accent, icon, testId }) {
  return (
    <div className="glass rounded-2xl p-4" data-testid={testId}>
      <div className="mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
        <span style={{ color: accent }}>{icon}</span>{label}
      </div>
      <div className="font-heading font-black text-3xl mt-2" style={{ color: accent }}>{value}</div>
    </div>
  );
}
